import { type AstArg, type AstNode, NO_POS, type SourcePos } from './ast.ts';
import { elemPath, kwargPath, nameKeyPath, paramPath } from './path.ts';
import { Dataset } from './value.ts';

/**
 * Normalized JS-object AST. The evaluator dispatches over this shape, not
 * the parser's AstNode tree. Two consequences:
 *
 * - The engine accepts pre-built JS objects too — an LLM can emit JSON
 *   directly and skip the parser entirely.
 * - Each op's Zod schema validates this shape.
 *
 * Shapes:
 * - Primitives (number / string / boolean / null / Date) — literal values
 * - `Dataset` instances — literal tabular values inserted by host code
 *   (the s-expr parser cannot produce these; they enter via direct
 *   JS-object input or as op return values)
 * - Arrays — vectors
 * - `{ $sym: 'name' }` — symbol references (looked up in env on evaluation)
 * - `{ name: 'op', params: [...], <kwarg>: ... }` — op calls
 */
export type AstInput =
  | number
  | string
  | boolean
  | null
  | Date
  | Dataset
  | readonly AstInput[]
  | { readonly $sym: string }
  | OpCall;

/**
 * An op call after normalization. The discriminator is `name`. Positional
 * args live in `params`; kwargs are top-level fields. Op schemas declare the
 * exact shapes they accept; the indexer is `unknown` here so type-narrowing
 * on sibling shapes (e.g. `{ $sym }`) isn't poisoned by an open
 * `AstInput`-typed indexer.
 */
export type OpCall = {
  readonly name: string;
  readonly params?: readonly AstInput[];
  readonly [kwarg: string]: unknown;
};
/**
 * Type guard: is this AST node an op call? An op call is a non-array
 * object with a string `name` field. Used by the evaluator's dispatch and
 * by `transform`'s verb-AST validation.
 */
export const isOpCall = (node: unknown): node is OpCall =>
  node !== null &&
  typeof node === 'object' &&
  !Array.isArray(node) &&
  'name' in node &&
  typeof (node as { name: unknown }).name === 'string';

/**
 * Tagged discriminant view over `AstInput`. The structural union has no
 * tag field, so every walker (`evaluator.evalNode`, `runtime/arquero.compileExpr`)
 * was repeating the same `null` / `typeof` / `instanceof` / `$sym` / `name`
 * chain to discriminate. `astShape` centralises that dispatch and gives
 * callers a tagged shape they can `switch` over with full exhaustiveness:
 * adding a new `AstInput` variant requires updating only `astShape`'s body
 * (and the `AstShape` union), and every consumer fails to compile until it
 * handles the new tag.
 */
export type AstShape =
  | { readonly kind: 'primitive'; readonly value: number | string | boolean | null }
  | { readonly kind: 'date'; readonly value: Date }
  | { readonly kind: 'dataset'; readonly value: Dataset }
  | { readonly kind: 'vector'; readonly elements: readonly AstInput[] }
  | { readonly kind: 'symbolRef'; readonly sym: string }
  | { readonly kind: 'opCall'; readonly call: OpCall }
  | { readonly kind: 'invalid'; readonly node: unknown };

/**
 * Discriminate an `AstInput`-shaped (or actually anything — accepts `unknown`
 * for the consumer's narrowing convenience) value to its tagged shape.
 * Returns `{ kind: 'invalid', node }` for anything outside the `AstInput`
 * surface; callers convert that to `ShapeError` with their own message.
 */
export const astShape = (node: unknown): AstShape => {
  if (node === null || typeof node === 'number' || typeof node === 'string' || typeof node === 'boolean') {
    return { kind: 'primitive', value: node };
  }
  if (node instanceof Date) {
    return { kind: 'date', value: node };
  }
  if (node instanceof Dataset) {
    return { kind: 'dataset', value: node };
  }
  if (Array.isArray(node)) {
    return { kind: 'vector', elements: node };
  }
  if (
    typeof node === 'object' &&
    '$sym' in node &&
    typeof (node as { $sym: unknown }).$sym === 'string' &&
    !('name' in node)
  ) {
    return { kind: 'symbolRef', sym: (node as { $sym: string }).$sym };
  }
  if (isOpCall(node)) {
    return { kind: 'opCall', call: node };
  }
  return { kind: 'invalid', node };
};

/**
 * Map from path-key (e.g. `/0/params/1`) to the source position of that
 * node. Populated by `normalize` and consumed by the evaluator to attach
 * positions to diagnostics. Direct JS-object input has no entries — its
 * diagnostics surface without source positions.
 */
export type Spans = ReadonlyMap<string, SourcePos>;
/**
 * Resolve the source position at a path, falling back to `NO_POS` when the
 * spans map has no entry (e.g. direct JS-object input).
 */
export const nodePos = (spans: Spans, path: string): SourcePos => spans.get(path) ?? NO_POS;

/**
 * Resolve the source position of an op call's head (its `name` token),
 * falling back to the call's overall position, then `NO_POS`. Used to
 * anchor `UnknownOpError`/`NotAnExpressionOpError`/`OpExecutionError` on
 * the op-name token rather than the wider call.
 */
export const opCallPos = (spans: Spans, path: string): SourcePos =>
  spans.get(nameKeyPath(path)) ?? spans.get(path) ?? NO_POS;

export type NormalizeResult = {
  readonly ast: readonly AstInput[];
  readonly spans: Spans;
};

/**
 * Convert a parsed AstNode[] script into the JS-object AST. The output keeps
 * one entry per top-level form. Source positions are extracted into the
 * spans sidecar so the JS objects are pure data.
 */
export const normalize = (forms: readonly AstNode[]): NormalizeResult => {
  const spans = new Map<string, SourcePos>();

  const walk = (node: AstNode, path: string): AstInput => {
    spans.set(path, node.pos);
    switch (node.kind) {
      case 'literal':
        return node.value;
      case 'symbol':
        // biome-ignore lint/style/useNamingConvention: $-prefixed engine reserved
        return { $sym: node.name };
      case 'vector':
        return node.elements.map((el, i) => walk(el, elemPath(path, i)));
      case 'list':
        return normalizeList(node.op, node.opPos, node.args, path);
      default: {
        // Exhaustiveness guard: a new `AstNode` kind fails to compile here
        // instead of being silently misclassified as a list.
        const unreachable: never = node;
        return unreachable;
      }
    }
  };

  const normalizeList = (op: string, opPos: SourcePos, args: readonly AstArg[], path: string): OpCall => {
    spans.set(nameKeyPath(path), opPos);
    const positional: AstInput[] = [];
    const kwargs: Record<string, AstInput> = {};
    args.forEach((arg) => {
      if (arg.kind === 'positional') {
        const i = positional.length;
        positional.push(walk(arg.value, paramPath(path, i)));
        return;
      }
      spans.set(kwargPath(path, arg.key), arg.keyPos);
      kwargs[arg.key] = walk(arg.value, kwargPath(path, arg.key));
    });
    const base: OpCall = { name: op, params: positional };
    return { ...base, ...kwargs };
  };

  const ast = forms.map((f, i) => walk(f, elemPath('', i)));
  return { ast, spans };
};
